import { HttpEvent, HttpEventType } from '@angular/common/http';
import { firstValueFrom, NEVER, Observable, of, throwError } from 'rxjs';
import { catchError, map, startWith, tap } from 'rxjs/operators';
import { $METADATA, DEFAULT_VERSION } from './constants';
import {
  ModelFieldOptions,
  ModelOptions,
  ODataCollection,
  ODataModel,
  ODataModelField,
  ODataModelOptions,
} from './models';
import { ODataApiOptions } from './options';
import type {
  EntityKey,
  ODataOptions,
  ODataPropertyResource,
  ODataResource,
  ODataSegment,
} from './resources';
import {
  ODataQueryableOptions,
  ODataPathSegments,
  ODataRequest,
  ODataResponse,
  ODataBatchResource,
  ODataMetadataResource,
  ODataActionResource,
  ODataFunctionResource,
  ODataEntityResource,
  ODataEntitySetResource,
  ODataSingletonResource,
  ODataNavigationPropertyResource,
  ODataQueryOptions,
} from './resources';
import {
  EDM_PARSERS,
  ODataCallable,
  ODataEntitySet,
  ODataEnumType,
  ODataSchema,
  ODataSingleton,
  ODataStructuredType,
} from './schema';
import {
  ODataApiConfig,
  EdmType,
  NONE_PARSER,
  ODataVersion,
  Parser,
  PathSegment,
  QueryOption,
  ODataSchemaConfig,
  ODataCache,
  ODataReferenceConfig,
} from './types';
import type { ODataMetadata } from './metadata/metadata';
import { ODataEntityAnnotations } from './annotations';
import { ODataReference } from './schema/reference';

const RESERVED_FIELD_NAMES = Object.getOwnPropertyNames(ODataModel.prototype);

/**
 * Api abstraction for consuming OData services.
 */
export class ODataApi {
  requester?: (request: ODataRequest<any>) => Observable<any>;
  serviceRootUrl: string;
  metadataUrl: string;
  name?: string;
  version: ODataVersion;
  default: boolean;
  creation: Date;
  // Options
  options: ODataApiOptions;
  // Cache
  cache?: ODataCache;
  // Error Handler
  errorHandler?: (error: any, caught: Observable<any>) => Observable<never>;
  // Base Parsers
  parsers: Map<string, Parser<any>>;
  // Populate from Metadata
  populateFromMetadata: boolean;
  // Schemas
  schemas: ODataSchema[] = [];
  // References
  references: ODataReference[] = [];
  // Models
  models: { [type: string]: typeof ODataModel<any> } = {};
  // Collections
  collections: { [type: string]: typeof ODataCollection<any, ODataModel<any>> } = {};

  constructor(config: ODataApiConfig) {
    this.serviceRootUrl = config.serviceRootUrl;
    if (this.serviceRootUrl.includes('?'))
      throw new Error(
        "The 'serviceRootUrl' should not contain query string. Please use 'params' to add extra parameters",
      );
    if (!this.serviceRootUrl.endsWith('/')) this.serviceRootUrl += '/';
    this.metadataUrl = config.metadataUrl ?? `${this.serviceRootUrl}${$METADATA}`;
    this.name = config.name;
    this.version = config.version ?? DEFAULT_VERSION;
    this.default = config.default ?? false;
    this.creation = config.creation ?? new Date();
    this.options = new ODataApiOptions({
      ...config.options,
      version: this.version,
    });

    this.cache = config.cache;
    this.errorHandler = config.errorHandler;
    this.parsers = new Map(Object.entries(config.parsers ?? EDM_PARSERS));

    this.populateFromMetadata = config.populateFromMetadata ?? false;
    this.populate(config);
  }

  initialize(requester: (request: ODataRequest<any>) => Observable<any>) {
    this.requester = requester;
    return (this.populateFromMetadata
      ? firstValueFrom(
          this.metadata()
            .fetch()
            .pipe(map((metadata) => this.populate(metadata.toConfig()))),
        )
      : Promise.resolve()).then(() => this.configure());
  }

  configure() {
    this.schemas.forEach((schema) => {
      schema.configure({
        options: this.options.parserOptions,
      });
    });
    Object.entries(this.models).forEach(([type, model]) => {
      const structured = this.findStructuredType<any>(type);
      if (structured !== undefined) {
        this.configureModel<any>(structured, model);
        const collection = this.collections[type];
        if (collection !== undefined) {
          collection.model = model;
        }
      }
    });
    return true;
  }

  private populate({
    schemas,
    references,
    models,
    collections
  }: {
    schemas?: ODataSchemaConfig[];
    references?: ODataReferenceConfig[];
    models?: { [type: string]: { new (...params: any[]): any } };
    collections?: { [type: string]: { new (...params: any[]): any } };
  }) {
    const odataSchemas = (schemas ?? []).map((schema) => new ODataSchema(schema, this));
    //TODO: Merge duplicates
    this.schemas = [...this.schemas, ...odataSchemas];
    const odataReferences = (references ?? []).map((reference) => new ODataReference(reference, this));
    this.references = [...this.references, ...odataReferences]; 
    const odataModels = (models ?? {}) as { [type: string]: typeof ODataModel<any> };
    this.models = { ...this.models, ...odataModels }; 
    const odataCollections = (collections ?? {}) as {
      [type: string]: typeof ODataCollection<any, ODataModel<any>>;
    };
    this.collections = { ...this.collections, ...odataCollections };
  }

  fromJson<P, R>(json: {
    segments: ODataSegment[];
    options: { [name: string]: any };
  }): ODataActionResource<P, R> | ODataFunctionResource<P, R>;
  fromJson<E>(json: {
    segments: ODataSegment[];
    options: { [name: string]: any };
  }):
    | ODataEntityResource<E>
    | ODataEntitySetResource<E>
    | ODataNavigationPropertyResource<E>
    | ODataSingletonResource<E>;
  fromJson(json: { segments: ODataSegment[]; options: { [name: string]: any } }) {
    const segments = ODataPathSegments.fromJson(json.segments);
    const query = ODataQueryOptions.fromJson<any>(json.options);
    switch (segments.last()?.name as PathSegment) {
      case PathSegment.entitySet:
        if (segments.last()?.hasKey()) {
          return new ODataEntityResource(this, { segments, query });
        } else {
          return new ODataEntitySetResource(this, { segments, query });
        }
      case PathSegment.navigationProperty:
        return new ODataNavigationPropertyResource(this, { segments, query });
      case PathSegment.singleton:
        return new ODataSingletonResource(this, { segments, query });
      case PathSegment.action:
        return new ODataActionResource(this, { segments, query });
      case PathSegment.function:
        return new ODataFunctionResource(this, { segments, query });
    }
    throw new Error('No Resource for json');
  }

  /**
   * Build a metadata resource.
   * @returns ODataMetadataResource
   */
  metadata(): ODataMetadataResource {
    return ODataMetadataResource.factory(this);
  }

  /**
   * Build a batch resource.
   * @returns ODataBatchResource
   */
  batch(): ODataBatchResource {
    return ODataBatchResource.factory(this);
  }

  /**
   * Build a singleton resource.
   * @param path Name of the singleton
   * @returns
   */
  singleton<T>(name: string) {
    const singleton = this.findSingleton(name);
    return ODataSingletonResource.factory<T>(this, {
      path: singleton?.name ?? name,
      type: singleton?.singletonType,
    });
  }

  /**
   * Build an entity set resource.
   * @param path Name of the entity set
   * @returns
   */
  entitySet<T>(name: string): ODataEntitySetResource<T> {
    const entitySet = this.findEntitySet(name);
    return ODataEntitySetResource.factory<T>(this, {
      path: entitySet?.name ?? name,
      type: entitySet?.entityType,
    });
  }

  /**
   * Unbound Action
   * @param  {string} path?
   * @returns ODataActionResource
   */
  action<P, R>(path: string): ODataActionResource<P, R> {
    const callable = this.findCallable<R>(path);
    return ODataActionResource.factory<P, R>(this, {
      path,
      outgoingType: callable?.type(),
      incomingType: callable?.returnType(),
    });
  }

  /**
   * Unbound Function
   * @param  {string} path?
   * @returns ODataFunctionResource
   */
  function<P, R>(path: string): ODataFunctionResource<P, R> {
    const callable = this.findCallable<R>(path);
    return ODataFunctionResource.factory<P, R>(this, {
      path,
      outgoingType: callable?.type(),
      incomingType: callable?.returnType(),
    });
  }

  callable<T>(type: string) {
    return this.findCallable<T>(type);
  }

  enumType<T>(type: string) {
    return this.findEnumType<T>(type);
  }

  structuredType<T>(type: string) {
    return this.findStructuredType<T>(type);
  }

  //request(req: ODataRequest<any>): Observable<any> {
  request<T>(
    method: string,
    resource: ODataResource<any>,
    options: ODataOptions & {
      body?: any;
      etag?: string;
      responseType?:
        | 'arraybuffer'
        | 'blob'
        | 'json'
        | 'text'
        | 'value'
        | 'property'
        | 'entity'
        | 'entities';
      observe?: 'body' | 'events' | 'response';
      withCount?: boolean;
      bodyQueryOptions?: QueryOption[];
    },
  ): Observable<any> {
    const req = ODataRequest.factory(this, method, resource, {
      body: options.body,
      etag: options.etag,
      context: options.context,
      headers: options.headers,
      params: options.params,
      responseType: options.responseType,
      observe: (options.observe === 'events' ? 'events' : 'response') as 'events' | 'response',
      withCount: options.withCount,
      bodyQueryOptions: options.bodyQueryOptions,
      reportProgress: options.reportProgress,
      fetchPolicy: options.fetchPolicy,
      maxAge: options.maxAge,
      parserOptions: options.parserOptions,
      withCredentials: options.withCredentials,
    });

    let res$ = this.handleRequest(req);

    if (this.errorHandler !== undefined) res$ = res$.pipe(catchError(this.errorHandler));

    if (options.observe === 'events') {
      return res$;
    }

    switch (options.observe || 'body') {
      case 'body':
        switch (options.responseType) {
          case 'entities':
            return res$.pipe(map((res: ODataResponse<T>) => res.entities()));
          case 'entity':
            return res$.pipe(map((res: ODataResponse<T>) => res.entity()));
          case 'property':
            return res$.pipe(map((res: ODataResponse<T>) => res.property()));
          case 'value':
            return res$.pipe(map((res: ODataResponse<T>) => res.value() as T));
          default:
            // Other responseTypes (arraybuffer, blob, json, text) return body
            return res$.pipe(map((res: ODataResponse<T>) => res.body));
        }
      case 'response':
        // The response stream was requested directly, so return it.
        return res$;
      default:
        // Guard against new future observe types being added.
        throw new Error(`Unreachable: unhandled observe type ${options.observe}}`);
    }
  }

  /**
   * Using the request, handle the fetching of the response
   * @param req The request to fetch
   * @returns
   */
  private handleRequest(req: ODataRequest<any>): Observable<any> {
    return this.cache !== undefined && req.isFetch()
      ? this.handleCacheFetch(req)
      : this.cache !== undefined && req.isMutate()
        ? this.handleCacheMutate(req)
        : this.handleRequester(req);
  }

  private handleRequester(req: ODataRequest<any>) {
    return (this.requester !== undefined ? this.requester(req) : NEVER).pipe(
      map((res: HttpEvent<any>) =>
        res.type === HttpEventType.Response ? ODataResponse.fromHttpResponse<any>(req, res) : res,
      ),
    );
  }

  private handleCacheFetch(req: ODataRequest<any>): Observable<any> {
    const policy = req.fetchPolicy;
    const cached = this.cache!.getResponse(req);
    if (policy === 'no-cache') {
      return this.handleRequester(req);
    }
    if (policy === 'cache-only') {
      if (cached !== undefined) {
        return of(cached);
      } else {
        return throwError(() => new Error('No Cached'));
      }
    }
    let res$: Observable<any> =
      cached !== undefined && policy !== 'network-only'
        ? policy === 'cache-and-network'
          ? this.handleRequester(req).pipe(startWith(cached))
          : of(cached)
        : this.handleRequester(req);
    if (
      cached === undefined &&
      (policy === 'cache-first' || policy === 'cache-and-network' || policy === 'network-only')
    ) {
      res$ = res$.pipe(
        tap((res: ODataResponse<any>) => {
          if (res.options.cacheability !== 'no-store') {
            this.cache!.putResponse(req, res);
          }
        }),
      );
    }
    return res$;
  }

  private handleCacheMutate(req: ODataRequest<any>): Observable<any> {
    const requests = req.isBatch()
      ? (req.resource as ODataBatchResource).requests().filter((r) => r.isMutate())
      : [req];
    for (var r of requests) {
      const scope = this.cache!.scope(r);
      this.cache!.forget({ scope });
    }
    return this.handleRequester(req);
  }

  //# region Find by Type
  // Memoize
  private memo: {
    enumTypes: Map<string, ODataEnumType<any> | undefined>;
    structuredTypes: Map<string, ODataStructuredType<any> | undefined>;
    callables: Map<string, ODataCallable<any> | undefined>;
    entitySets: Map<string, ODataEntitySet | undefined>;
    singletons: Map<string, ODataSingleton | undefined>;
    parsers: Map<string, Parser<any>>;
    options: Map<string, ODataModelOptions<any> | undefined>;
  } = {
    enumTypes: new Map<string, ODataEnumType<any> | undefined>(),
    structuredTypes: new Map<string, ODataStructuredType<any> | undefined>(),
    callables: new Map<string, ODataCallable<any> | undefined>(),
    entitySets: new Map<string, ODataEntitySet | undefined>(),
    singletons: new Map<string, ODataSingleton | undefined>(),
    parsers: new Map<string, Parser<any>>(),
    options: new Map<string, ODataModelOptions<any> | undefined>(),
  };

  public createSchema(config: ODataSchemaConfig) {
    const schema = new ODataSchema(config, this);
    schema.configure({
      options: this.options.parserOptions,
    });
    this.schemas.push(schema);
    return schema;
  }

  public findSchema(type: string) {
    const schemas = this.schemas.filter((s) => s.isNamespaceOf(type));
    if (schemas.length === 0) return undefined;
    if (schemas.length === 1) return schemas[0];
    return schemas.sort((s1, s2) => s1.namespace.length - s2.namespace.length).pop();
  }

  //#region EnumTypes
  public findEnumType<T>(value: string) {
    if (this.memo.enumTypes.has(value)) {
      return this.memo.enumTypes.get(value) as ODataEnumType<T> | undefined;
    }
    const enumTypes = this.schemas.reduce(
      (acc, schema) => [...acc, ...schema.enums],
      <ODataEnumType<any>[]>[],
    );
    let enumType = enumTypes.find((e) => e.type() === value);
    enumType = enumType ?? enumTypes.find((e) => e.type({ alias: true }) === value);
    enumType = enumType ?? enumTypes.find((e) => e.name === value);
    this.memo.enumTypes.set(value, enumType);
    return enumType as ODataEnumType<T> | undefined;
  }
  //#endregion

  //#region StructuredTypes
  public findStructuredType<T>(value: string) {
    if (this.memo.structuredTypes.has(value)) {
      return this.memo.structuredTypes.get(value) as ODataStructuredType<T> | undefined;
    }
    const structuredTypes = this.schemas.reduce(
      (acc, schema) => [...acc, ...schema.entities],
      <ODataStructuredType<any>[]>[],
    );
    let structuredType = structuredTypes.find((e) => e.type() === value);
    structuredType =
      structuredType ?? structuredTypes.find((e) => e.type({ alias: true }) === value);
    structuredType = structuredType ?? structuredTypes.find((e) => e.name === value);
    this.memo.structuredTypes.set(value, structuredType);
    return structuredType as ODataStructuredType<T> | undefined;
  }
  //#endregion

  //#region Callables
  public findCallable<R>(value: string, bindingType?: string) {
    const key = bindingType !== undefined ? `${bindingType}/${value}` : value;
    if (this.memo.callables.has(key)) {
      return this.memo.callables.get(key) as ODataCallable<R> | undefined;
    }

    const bindingStructuredType =
      bindingType !== undefined ? this.findStructuredType<any>(bindingType) : undefined;
    const callables = this.schemas.reduce(
      (acc, schema) => [...acc, ...schema.callables],
      <ODataCallable<any>[]>[],
    );
    let callable = callables.find((c) => {
      const isCallableType = c.type() == value;
      const callableBindingType = c.binding()?.type;
      const callableBindingStructuredType =
        callableBindingType !== undefined
          ? this.findStructuredType<any>(callableBindingType)
          : undefined;

      return (
        isCallableType &&
        (!bindingStructuredType ||
          (callableBindingStructuredType &&
            bindingStructuredType.isSubtypeOf(callableBindingStructuredType)))
      );
    });
    callable =
      callable ??
      callables.find((c) => {
        const isCallableType = c.type({ alias: true }) == value;
        const callableBindingType = c.binding()?.type;
        const callableBindingStructuredType =
          callableBindingType !== undefined
            ? this.findStructuredType<any>(callableBindingType)
            : undefined;

        return (
          isCallableType &&
          (!bindingStructuredType ||
            (callableBindingStructuredType &&
              bindingStructuredType.isSubtypeOf(callableBindingStructuredType)))
        );
      });
    callable =
      callable ??
      callables.find((c) => {
        const isCallableType = c.name == value;
        const callableBindingType = c.binding()?.type;
        const callableBindingStructuredType =
          callableBindingType !== undefined
            ? this.findStructuredType<any>(callableBindingType)
            : undefined;

        return (
          isCallableType &&
          (!bindingStructuredType ||
            (callableBindingStructuredType &&
              bindingStructuredType.isSubtypeOf(callableBindingStructuredType)))
        );
      });

    this.memo.callables.set(key, callable);
    return callable as ODataCallable<R> | undefined;
  }
  //#endregion

  //#region EntitySets
  public findEntitySet(value: string) {
    if (this.memo.entitySets.has(value)) {
      return this.memo.entitySets.get(value) as ODataEntitySet | undefined;
    }
    const entitySets = this.schemas.reduce(
      (acc, schema) => [...acc, ...schema.entitySets],
      <ODataEntitySet[]>[],
    );
    let entitySet = entitySets.find((e) => e.type() === value);
    entitySet = entitySet ?? entitySets.find((e) => e.type({ alias: true }) === value);
    entitySet = entitySet ?? entitySets.find((e) => e.name === value);
    this.memo.entitySets.set(value, entitySet);
    return entitySet as ODataEntitySet | undefined;
  }
  //#endregion

  //#region Singletons
  public findSingleton(value: string) {
    if (this.memo.singletons.has(value)) {
      return this.memo.singletons.get(value) as ODataSingleton | undefined;
    }
    const singletons = this.schemas.reduce(
      (acc, schema) => [...acc, ...schema.singletons],
      <ODataSingleton[]>[],
    );
    let singleton = singletons.find((e) => e.type() === value);
    singleton = singleton ?? singletons.find((e) => e.type({ alias: true }) === value);
    singleton = singleton ?? singletons.find((e) => e.name === value);
    this.memo.singletons.set(value, singleton);
    return singleton as ODataSingleton | undefined;
  }
  //#endregion

  public configureModel<T>(structured: ODataStructuredType<T>, model: typeof ODataModel<T>) {
    model.meta = this.optionsForType<T>(structured.type(), {
      config: model.options,
      structuredType: structured,
    })!;
    if (model.meta !== undefined) {
      // Configure
      model.meta.configure({ options: this.options.parserOptions });
    }
  }

  public findModel<T>(type: string) {
    return (this.models[type] ?? this.findStructuredType<any>(type)?.model) as
      | typeof ODataModel<T>
      | undefined;
  }

  public createModel<T>(structured: ODataStructuredType<T>) {
    if (structured.model !== undefined) return structured.model;
    // Build Ad-hoc model
    const Model = class extends ODataModel<T> {} as typeof ODataModel<T>;
    // Store New Model structured for next time
    structured.model = Model;
    return Model;
  }

  public modelForType<T>(type: string) {
    let Model = this.findModel<T>(type);
    if (Model === undefined) {
      const structured = this.findStructuredType<T>(type);
      if (structured === undefined) throw Error(`No structured type for ${type}`);
      Model = this.createModel<T>(structured);
      this.configureModel<T>(structured, Model);
    }
    return Model;
  }

  public findCollection<T>(type: string) {
    return (this.collections[type] ?? this.findStructuredType<any>(type)?.collection) as
      | typeof ODataCollection<T, ODataModel<T>>
      | undefined;
  }

  public createCollection<T>(structured: ODataStructuredType<T>, model?: typeof ODataModel<T>) {
    if (structured.collection !== undefined) return structured.collection;
    if (model === undefined) model = this.createModel(structured);
    // Build Ad-hoc collection
    const Collection = class extends ODataCollection<T, ODataModel<T>> {
      static override model = model!;
    } as typeof ODataCollection<T, ODataModel<T>>;
    // Store New Collection structured for next time
    structured.collection = Collection;
    return Collection;
  }

  public collectionForType<T>(type: string) {
    let Collection = this.findCollection<T>(type);
    if (Collection === undefined) {
      const structured = this.findStructuredType<T>(type);
      if (structured === undefined) throw Error(`No structured type for ${type}`);
      const Model = this.modelForType<T>(type);
      Collection = this.createCollection<T>(structured, Model) as typeof ODataCollection<
        T,
        ODataModel<T>
      >;
    }
    return Collection;
  }

  public findEntitySetForEntityType(entityType: string) {
    if (this.memo.entitySets.has(entityType)) {
      return this.memo.entitySets.get(entityType) as ODataEntitySet | undefined;
    }
    const entitySet = this.schemas
      .reduce((acc, schema) => [...acc, ...schema.entitySets], <ODataEntitySet[]>[])
      .find((e) => e.entityType === entityType);
    this.memo.entitySets.set(entityType, entitySet);
    return entitySet;
  }

  public parserForType<T>(type: string | EdmType, bindingType?: string) {
    const key = bindingType !== undefined ? `${bindingType}/${type}` : type;
    if (this.memo.parsers.has(key)) {
      return this.memo.parsers.get(key) as Parser<T>;
    }
    // None Parser by default
    let parser: Parser<T> = NONE_PARSER;
    if (this.parsers.has(type)) {
      // Edm, Base Parsers
      parser = this.parsers.get(type) as Parser<T>;
    } else if (!type.startsWith('Edm.')) {
      // Callable, EnumType, StructuredType (ComplexType and EntityType) Parsers
      let value =
        this.findCallable<T>(type, bindingType) ??
        this.findEnumType<T>(type) ??
        this.findStructuredType<T>(type);
      parser = value?.parser as Parser<T>;
    }
    // Set Parser for next time
    this.memo.parsers.set(key, parser);
    return parser;
  }

  public optionsForType<T>(
    type: string,
    {
      structuredType,
      config,
    }: { structuredType?: ODataStructuredType<T>; config?: ModelOptions } = {},
  ) {
    // Strucutred Options
    if (this.memo.options.has(type)) {
      return this.memo.options.get(type) as ODataModelOptions<T> | undefined;
    }

    let meta: ODataModelOptions<T> | undefined = undefined;
    structuredType = this.findStructuredType<T>(type) ?? structuredType;
    if (structuredType !== undefined) {
      if (config === undefined) {
        const fields = structuredType
          .fields({ include_navigation: true, include_parents: true })
          .reduce((acc, field) => {
            let name = field.name;
            // Prevent collision with reserved keywords
            while (RESERVED_FIELD_NAMES.includes(name)) {
              name = name + '_';
            }
            return Object.assign(acc, {
              [name]: {
                field: field.name,
                default: field.default,
                required: !field.nullable,
              },
            });
          }, {});
        config = {
          fields: new Map<string, ModelFieldOptions>(Object.entries(fields)),
        };
      }
      meta = new ODataModelOptions<T>({ config, structuredType });
    }
    // Set Options for next time
    this.memo.options.set(type, meta);
    return meta;
  }
}
